import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Clock, Globe, ChevronDown, Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Header = () => {
  const { t, i18n } = useTranslation();
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSubmenu, setMobileSubmenu] = useState(null);
  
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const menuItems = [
    {
      name: t('schengen'),
      items: [
        { name: t('urgentSchengenNav'), path: '/services/urgent-schengen-visa' },
        { name: t('visaToItaly'), path: '/services/italian-visa' },
        { name: t('visaToSpain'), path: '/services/spanish-visa' },
        { name: t('visaToFrance'), path: '/services/french-visa' },
        { name: t('visaToSwitzerland'), path: '/services/switzerland-visa' },
        { name: t('schengenForDomesticWorkers'), path: '/services/visa-for-domestic-workers' },
      ]
    },
    {
      name: t('usaUk'),
      items: [
        { name: t('urgentUSAVisa'), path: '/services/usa-urgent-visa' },
        { name: t('regularUSVisa'), path: '/services/us-visa' },
        { name: t('renewingUSVisa'), path: '/services/renewing-us-visa-in-dubai' },
        { name: t('usStudentVisaF1'), path: '/services/usa-study-visa' },
        { name: t('urgentUKVisa'), path: '/services/urgent-uk-visa' },
        { name: t('regularUKVisa'), path: '/services/uk-visa-2' },
        { name: t('ukStudentVisa'), path: '/services/uk-student-visa' },
        { name: t('visaForDomesticWorkers'), path: '/services/visa-for-domestic-workers' },
      ]
    },
    {
      name: t('otherVisas'),
      items: [
        { name: t('visaToJapan'), path: '/services/japan-visa' },
        { name: t('visaToChina'), path: '/services/china-visa' },
        { name: t('visaToSouthAfrica'), path: '/services/visa-to-south-africa' },
        { name: t('visaToCanada'), path: '/services/canada-visa' },
        { name: t('visaToRussia'), path: '/services/russia-visa' },
        { name: t('visaToCyprus'), path: '/services/cyprus' },
        { name: t('visaToAustralia'), path: '/services/australia-visa' },
        { name: t('visaToSaudiArabia'), path: '/services/saudi-arabia-visa' },
        { name: t('visaToTurkey'), path: '/services/turkey-visa' },
        { name: t('visaToSingapore'), path: '/services/singapore-visa' },
        { name: t('visaToNewZealand'), path: '/services/new-zealand-visa' },
        { name: t('visaToBali'), path: '/services/visa-to-bali' },
      ]
    },
    {
      name: t('uaeServices'),
      items: [
        { name: t('digitalNomadVisa'), path: '/services/digital-nomad-visa' },
        { name: t('freelanceVisa'), path: '/services/uae-freelance-visa' },
        { name: t('goldenVisa'), path: '/services/uae-golden-visa' },
        { name: t('bankAccountOpening'), path: '/services/uae-bank-account' },
        { name: t('businessSetupInUAE'), path: '/services/uae-business-setup' },
      ]
    },
    {
      name: t('aboutUs'),
      items: [
        { name: t('aboutUsPage'), path: '/about' },
        { name: t('contactUsPage'), path: '/contact' },
        { name: t('affiliateProgram'), path: '/partnership' },
        { name: t('careers'), path: '/careers' },
      ]
    }
  ];

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileSubmenu(null);
  };

  return (
    <header className="w-full bg-white shadow-sm relative z-50">
      {/* Top Bar */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-10 text-sm">
            <div className="flex items-center space-x-6">
              <a href="tel:+971585862103" className="flex items-center text-gray-700 hover:text-blue-700 transition-colors">
                <Phone className="w-4 h-4 mr-1 flex-shrink-0" />
                <span className="whitespace-nowrap text-xs sm:text-sm">+971 58 586 2103</span>
              </a>
              <div className="hidden sm:flex items-center text-gray-700">
                <Clock className="w-4 h-4 mr-2" />
                {t('hours')}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Globe className="w-4 h-4 text-gray-700" />
                <select
                  value={i18n.language}
                  onChange={(e) => changeLanguage(e.target.value)}
                  className="bg-transparent border-none text-gray-700 text-sm focus:outline-none cursor-pointer"
                >
                  <option value="en">🇬🇧 EN</option>
                  <option value="ar">🇸🇦 AR</option>
                  <option value="ru">🇷🇺 RU</option>
                </select>
              </div>
              <a
                href="https://wa.me/971585862103"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-green-600 hover:text-green-700 transition-colors font-medium"
              >
                {t('whatsapp')}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3" onClick={closeMobile}>
            <img 
              src="/logo.png" 
              alt="The Visagram" 
              className="h-10 lg:h-12 w-auto"
            />
            <div className="flex flex-col">
              <span className="text-xl lg:text-2xl font-bold text-gray-900">The Visagram</span>
              <span className="text-xs text-gray-600 -mt-1">Visa Services Agency</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {menuItems.map((menu, menuIndex) => (
              <div
                key={menu.name}
                className="relative"
                onMouseEnter={() => setOpenMenu(menuIndex)}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <button className="flex items-center space-x-1 px-4 py-2 text-blue-700 font-semibold hover:text-blue-600 transition-colors">
                  <span>{menu.name}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${openMenu === menuIndex ? 'rotate-180' : ''}`} />
                </button>
                
                {openMenu === menuIndex && (
                  <div className="absolute top-full left-0 mt-0 w-64 bg-white shadow-xl rounded-lg overflow-hidden border border-gray-100 animate-fadeIn">
                    <div className="py-2">
                      {menu.items.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className="block px-6 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                          onClick={() => setOpenMenu(null)}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Desktop CTA */}
          <a
            href="https://wa.me/971585862103?text=Hello%2C%20I%20would%20like%20a%20free%20consultation%20about%20visa%20services"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:block bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-6 py-3 rounded-full font-semibold hover:from-blue-700 hover:to-logo-cyan transition-all shadow-md hover:shadow-lg transform hover:scale-105"
          >
            {t('getConsultation')}
          </a>

          {/* Mobile Hamburger */}
          <button
            className="lg:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 top-[104px] bg-white z-50 overflow-y-auto" data-testid="mobile-menu">
          <nav className="px-4 py-4">
            {menuItems.map((menu, menuIndex) => (
              <div key={menu.name} className="border-b border-gray-100">
                <button
                  className="w-full flex items-center justify-between py-3 px-2 text-gray-900 font-semibold"
                  onClick={() => setMobileSubmenu(mobileSubmenu === menuIndex ? null : menuIndex)}
                  data-testid={`mobile-menu-${menuIndex}`}
                >
                  <span>{menu.name}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${mobileSubmenu === menuIndex ? 'rotate-180' : ''}`} />
                </button>
                {mobileSubmenu === menuIndex && (
                  <div className="pb-2">
                    {menu.items.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="block py-2.5 px-6 text-sm text-gray-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                        onClick={closeMobile}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Mobile CTA */}
            <div className="mt-4 px-2">
              <a
                href="https://wa.me/971585862103?text=Hello%2C%20I%20would%20like%20a%20free%20consultation%20about%20visa%20services"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-6 py-3 rounded-full font-semibold shadow-md"
                onClick={closeMobile}
              >
                {t('getConsultation')}
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
